// ============================================
// src/App/assignConsentGate.js
// MA-61 assign slice — teacher consent gate for ASSIGNING a skill to a
// linked student (pure core, mirrors receiptConsentGate.js's shape exactly).
//
// WHY A SEPARATE GATE (not reuse of the receipt-view consent): that
// consent's copy ("...authorized to view this student's learning data...")
// is scoped to VIEWING a student's past practice/misconception history — it
// never tells the teacher (or the school) that the teacher will also be
// able to STEER what the student practices next. Directing a child's work
// is a materially different disclosure than reading about it, so it gets
// its own consent lane rather than silently riding the viewing one (the
// red-team finding that shaped this file: reusing receipt-view consent for
// assign would be a category error, conflating disclosure with
// intervention). Per the standing build-loop rule, new/changed disclosure
// copy is the one thing this loop does not autonomously ship live text for
// — the exact wording below is a DRAFT pending founder/legal sign-off (see
// docs/legal/PENDING-COPY.md); the CODE PATH (the gate + its wiring) ships
// now so the feature is fully built and inert-safe (fails closed to "not
// consented") until the copy is approved.
//
// Reuses the SAME `consent_records` table + RLS (`parent_id = auth.uid()`)
// as receiptConsentGate.js/schoolConsentGate.js — a teacher's own
// authenticated session already satisfies the existing INSERT/SELECT
// policies with no schema change (consent_type is a bare, unconstrained
// text column).
//
// SCOPE (MA-937, D-TUX-25 — rewritten from the per-student-only rationale).
// Two rows satisfy this gate: the per-student row `assign_v1:<studentId>`
// (every existing row keeps working), and the CLASS row
// `class_practice_v1:<classId>` (receiptConsentGate.js) for a child with a
// LIVE enrollment in that class, children added later included. The class
// card's DRAFT copy names both acts (viewing in-class practice and choosing
// what the class practises next, which for a linked child reaches its home
// practice), so one confirmation covers both lanes for the class. The scope
// used to be per student because a revoked roster row can be re-claimed by a
// different child, and because steering a child the teacher could also read
// at home was a per-family matter; the class row only covers a child the
// CURRENT roster lists live (a revoked row covers no one), and it counts only
// once MA-936's migration narrows the teacher's view to in-class practice (the
// MA-936 lane read answers "live"; 42703 / PGRST202 / 42883 = not applied, the
// per-student cards stay; "pending"/"unknown" fail closed). The ONE switch
// CLASS_PRACTICE_ACK_ENABLED (re-exported here) brings the per-student cards
// back if counsel requires them.
//
// NOTE ON SECURITY VS. CONSENT (see 0027_teacher_skill_assignment.sql): the
// ACTUAL write-authorization boundary is enforced server-side by the
// assign_skill_to_student RPC (re-derives current class_enrollments/classes
// linkage) — this gate is a client-side disclosure/compliance step, not a
// security control, matching every other consent gate in this codebase
// (e.g. TeacherReceiptPanel's RLS access already exists before its consent
// card is even shown).
// ============================================

import { CLASS_PRACTICE_ACK_ENABLED, coveredByClassPractice } from "./receiptConsentGate.js";

export { CLASS_PRACTICE_ACK_ENABLED };

export const ASSIGN_CONSENT_PREFIX = "assign_v1";

/** `assign_v1:<studentId>` — the value written to/matched against consent_type. */
export function assignConsentType(studentId) {
  return `${ASSIGN_CONSENT_PREFIX}:${studentId}`;
}

/**
 * True iff `rows` contains a GRANTED, un-revoked assign consent row scoped
 * to EXACTLY this `studentId`. A consent granted for a different student
 * (or a non-assign consent, e.g. `receipt_view_v1_*`) never satisfies this
 * gate on its own; MA-937's optional arguments (`liveEnrollments`: child id
 * -> live class ids; `lane`: the MA-936 lane read) also admit the class row.
 * @param {any} rows
 * @param {string} studentId
 * @param {Readonly<Record<string, readonly string[]>> | null} [liveEnrollments]
 * @param {{ state: string } | null} [lane]
 * @param {boolean} [enabled]
 * @returns {boolean}
 */
export function hasAssignConsent(rows, studentId, liveEnrollments = null, lane = null, enabled = CLASS_PRACTICE_ACK_ENABLED) {
  if (!Array.isArray(rows)) return false;
  const type = assignConsentType(studentId);
  const perStudent = rows.some(
    (r) =>
      r &&
      r.granted === true &&
      (r.revoked_at === null || r.revoked_at === undefined) &&
      r.consent_type === type,
  );
  if (perStudent) return true;
  // MA-937: or the class row, for a child live-enrolled in that class.
  return coveredByClassPractice(rows, studentId, liveEnrollments, lane, enabled);
}

/**
 * The gate decision. Dev bypass short-circuits (e2e/dev has no real
 * session) — SAME policy as the receipt-view/school-authority guards. A
 * read error is treated as NO consent (fail-CLOSED): a flaky fetch must
 * never open the gate.
 */
export async function guardAssignSkillWrite({ studentId, teacherId, fetchConsent, devBypass = false, liveEnrollments = null, lane = null }) {
  if (devBypass) return { allow: true, reason: "dev-bypass" };
  let rows = [];
  try {
    rows = await fetchConsent(teacherId);
  } catch {
    rows = [];
  }
  return hasAssignConsent(rows, studentId, liveEnrollments, lane)
    ? { allow: true, reason: "consented" }
    : { allow: false, reason: "no-consent" };
}
